// Saved filter views for the /ar list (ART-06).
//
// A "view" is a named snapshot of the filter row's state. Views persist as a
// JSON array under the `ar.savedViews` localStorage key so they survive
// reloads on the same browser. `saveView` upserts by name (re-saving an
// existing name overwrites its state rather than creating a duplicate).

import type { ArTab } from './ar-tabs';

const STORAGE_KEY = 'ar.savedViews';

/** A named snapshot of the /ar filter row. */
export interface ArView {
  name: string;
  state: {
    tab: ArTab;
    q: string;
    branchId: string;
    departmentId: string;
    termsFilter: string;
    dateFrom: string;
    dateTo: string;
  };
}

/**
 * Read all saved views. Returns an empty array when nothing is stored or the
 * stored value is unparsable/malformed, so callers never have to guard.
 */
export function loadViews(): ArView[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ArView[]) : [];
  } catch {
    return [];
  }
}

function persist(views: ArView[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
}

/** Upsert a view by name (latest state wins), preserving order. */
export function saveView(view: ArView): void {
  const views = loadViews();
  const idx = views.findIndex((v) => v.name === view.name);
  if (idx === -1) views.push(view);
  else views[idx] = view;
  persist(views);
}

/** Remove a saved view by name. No-op when the name is absent. */
export function deleteView(name: string): void {
  persist(loadViews().filter((v) => v.name !== name));
}
